/**
 * Image optimization utilities.
 * Wraps Firebase Storage URLs through the /api/img proxy Cloud Function
 * to serve resized WebP images with CDN caching.
 * 
 * In dev mode (Vite dev server), the proxy is not available so we fallback
 * to the raw Firebase Storage URL to avoid broken images.
 */

import { Capacitor } from '@capacitor/core';

const IS_DEV = import.meta.env.DEV;
const IS_NATIVE = Capacitor.isNativePlatform();
const PROXY_PATH = '/api/img';
// En natif, l'app est servie depuis capacitor://localhost : le proxy doit
// etre appele en absolu sur le domaine de prod (WebP redimensionne + CDN).
const PROXY_ORIGIN = 'https://www.clocook.fr';

/**
 * Returns an optimized proxy URL for Firebase Storage images.
 * Non-Storage URLs pass through unchanged.
 * In dev mode or native app, returns the raw URL (no proxy available).
 * 
 * @param {string|null} url - Original image URL
 * @param {Object} opts
 * @param {number} opts.width - Target width (default 800)
 * @param {number} opts.quality - WebP quality 10-100 (default 75)
 * @returns {string|null}
 */
export function optimizedUrl(url, { width = 800, quality = 75 } = {}) {
  if (!url) return null;
  if (!url.includes('firebasestorage.googleapis.com')) return url;
  const query = `url=${encodeURIComponent(url)}&w=${width}&q=${quality}`;
  // En natif (Capacitor), proxy en absolu sur le domaine de prod
  if (IS_NATIVE) return `${PROXY_ORIGIN}${PROXY_PATH}?${query}`;
  // En dev, le proxy Vite redirige /api/img vers l'émulateur Functions
  return `${PROXY_PATH}?${query}`;
}

/**
 * Generates a srcset string for responsive images.
 * 
 * @param {string|null} url - Original image URL
 * @param {number[]} widths - Array of widths for srcset (default [400, 800])
 * @param {number} quality - WebP quality (default 75)
 * @returns {string}
 */
export function optimizedSrcset(url, widths = [400, 800], quality = 75) {
  if (!url || !url.includes('firebasestorage.googleapis.com')) return '';
  // In dev or native, no proxy → no srcset (browser uses src directly)
  if (IS_DEV || IS_NATIVE) return '';
  return widths.map(w => `${optimizedUrl(url, { width: w, quality })} ${w}w`).join(', ');
}
